import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import type { QuestionTypes } from "@/types";
import { useTranslation } from "react-i18next";
import { useFormResponseStore } from "@/store";
import React, { useEffect, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { formatInTimezone } from "@/utils/timezone";

interface DateProps {
  question: QuestionTypes;
  sectionId: string;
  sectionName: string;
  setValidationErrors: (    
    sectionId: string,
    questionId: string,
    error: string | null
  ) => void;
}

export const DateAnswer: React.FC<DateProps> = ({
  question,
  sectionId,
  sectionName,
  setValidationErrors,
}) => {
  const { t } = useTranslation();
  const { setResponse, formResponses } = useFormResponseStore();
  const existingSection = formResponses[sectionId];
  const existingResponse = existingSection?.questions[question.id]?.response || null;

  const resolveDefaultDate = (): Date | undefined => {
    if (existingResponse) {
      if (typeof existingResponse === 'string') {
        const date = new Date(existingResponse);
        return !isNaN(date.getTime()) ? date : undefined;
      } else if (existingResponse.date) {
        const date = new Date(existingResponse.date);
        return !isNaN(date.getTime()) ? date : undefined;
      } else if (existingResponse.start) {
        const date = new Date(existingResponse.start);
        return !isNaN(date.getTime()) ? date : undefined;
      }
    }
    if (question.defaultValue === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    }
    return undefined;
  };

  const [singleDate, setSingleDate] = useState<Date | undefined>(resolveDefaultDate);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    setSingleDate(date);
    // Clear validation error and close the calendar once a date is picked
    if (date) {
      setValidationErrors(sectionId, question.id, null);
      setIsCalendarOpen(false);
    }
  };

  // Initial validation on mount - runs ONCE like other components
  useEffect(() => {
    if (question.required === 'yes') {
      if (!singleDate) {
        setValidationErrors(sectionId, question.id, `${question.label} ${t('forms.isRequired')}`);
      } else {
        setValidationErrors(sectionId, question.id, null);
      }
    }
  }, []); // Empty deps - runs only on mount

  useEffect(() => {
    if (singleDate) {
      const response = {
        date: format(singleDate, "yyyy-MM-dd"),
      };
      setResponse(
        sectionId,
        sectionName,
        question.id,
        question.type,
        question.label ?? "",
        response
      );
    }
  }, [singleDate]);

  const dateFormat = question.dateFormat || 'LLL dd, y';

  const formatDateForDisplay = (date: Date) => {
    if (question.timezone) {
      try {
        return formatInTimezone(date, question.timezone, {
          year: 'numeric',
          month: dateFormat === 'LLL dd, y' ? 'long' : '2-digit',
          day: '2-digit'
        });
      } catch {
        // fall through to local format
      }
    }
    return format(date, dateFormat);
  };

  const renderDateInput = () => {
    switch (question.timeType) {
      case "all-time":
        return (
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <button className="rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm w-full outline-none mt-1">
                {singleDate ? (
                  formatDateForDisplay(singleDate)
                ) : (
                  <span>{t('forms.pickADate')}</span>
                )}
                <CalendarIcon className="mr-2 h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={singleDate}
                onSelect={handleDateSelect}
                captionLayout="dropdown-buttons"
                fromYear={1900}
                numberOfMonths={1}
                toYear={2050}
              />
            </PopoverContent>
          </Popover>
        );
      case "past-only":
        return (
          <div className="">
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <button className="rounded border-l-[2.4px] border-primary px-5 py-3 flex gap-6 justify-between text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm w-full outline-none mt-1">
                  <div className="flex items-center gap-6">
                    {singleDate ? (
                      formatDateForDisplay(singleDate)
                    ) : (
                      <span>{t('forms.pickADate')}</span>
                    )}
                  </div>

                  <CalendarIcon className="mr-1 h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={singleDate}
                  onSelect={handleDateSelect}
                  captionLayout="dropdown-buttons"
                  fromYear={1900}
                  toDate={new Date()}
                  numberOfMonths={1}
                />
              </PopoverContent>
            </Popover>
          </div>
        );
      case "future-only":
        return (
          <div className="">
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <button className="rounded border-l-[2.4px] border-primary px-5 py-3 flex  gap-6 justify-between text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm w-full outline-none mt-1">
                  <div className="flex items-center gap-6">
                    {singleDate ? (
                      formatDateForDisplay(singleDate)
                    ) : (
                      <span>{t('forms.pickADate')}</span>
                    )}
                  </div>
                  <CalendarIcon className="mr-1 h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={singleDate}
                  onSelect={handleDateSelect}
                  captionLayout="dropdown-buttons"
                  fromDate={new Date()}
                  numberOfMonths={1}
                  toYear={2100}
                />
              </PopoverContent>
            </Popover>
          </div>
        );
      default:
        return <span>{t('forms.pleaseSelectValidDateType')}</span>;
    }
  };

  return (
    <div className="mb-4">
      <div className="main-label">
        {question.label}{" "}
        {question.required == "yes" && <span className="text-red-500">*</span>}
      </div>
      {renderDateInput()}
    </div>
  );
};
